import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useContext, useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  Grid,
  TextField,
  Typography,
  Stack,
  Alert,
  Snackbar,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Autocomplete,
  SelectChangeEvent,
  Tabs,
  Tab,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import PageArea from '../../components/shared/PageArea';
import PageTitle from '../../components/shared/PageTitle';
import { Section } from '../../components/ui/Section';
import { UserContext } from '../../components/shared/useUser';
import { MarketStore, Instance, Market, MarketCatalogue, MarketOrderType, MarketSupplier } from '../../app/services/api/orderManagementClient';
import { client } from '../../app/services/api/orderManagementClient';
import useLogError from '../../hooks/useLogError';
import { isOrderTypeDSD } from '../../enums/OrderType';

/** Site Detail (Demo Mode: Preview) - nested detail screen for a single site, opened by
 * drilling into a row on the Sites list (Overview tab of EstateManagement/Sites.tsx, which
 * embeds the real Store Portfolio list unmodified). The "Overview" tab here is a copy of the
 * real StoreForm edit/create logic (real API load/save, unmodified fields) so the site record
 * stays backed by the real Store data. The remaining tabs (Customer & Contract, Operations,
 * Zones, Equipment, Users, Procurement Setup, Compliance, Reporting, Documents) are new
 * estate-management concepts with no backend yet, so they use static mock data - extended
 * to reflect the loaded store's own name/instance/market where available - matching the
 * "seeded illustrative numbers" approach used elsewhere in EstateManagement. */

// Illustrative data for tabs with no backend yet, layered on top of the real store record.
const EXAMPLE = {
  zones: ['Main Kitchen', 'Servery', 'Chilled Store'],
  equipmentCount: 11,
  users: [
    { name: 'A. Kensington', role: 'Site Manager' },
    { name: 'D. Osei', role: 'Head Chef' },
  ],
  procurement: 'Standard estate catalogue, Bidfood primary supplier',
  compliance: 'Up to date - last audit 2026-04-02',
  reporting: 'Weekly ops pack, monthly customer review',
  contract: 'TF-2024-SITE (3 year, renews 2027)',
  customer: 'Thomas Franks',
};

const SiteDetail = () => {
  const { t } = useTranslation('stores');
  const { t: tc } = useTranslation('common');
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logError, extractErrorMessage } = useLogError();
  const { selectedSystem } = useContext(UserContext);
  const [activeTab, setActiveTab] = useState(0);

  const isEditMode = Boolean(id);
  const companyId = searchParams.get('companyId');
  const customerTypeId = searchParams.get('customerTypeId');
  const searchTerm = searchParams.get('searchTerm');
  const [isLoading, setIsLoading] = useState<boolean>(isEditMode);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState<boolean>(false);
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const [formData, setFormData] = useState<MarketStore>({
    customerId: 0,
    companyId: parseInt(companyId || '0'),
    instanceName: '',
    customerTypeId: parseInt(customerTypeId || '0'),
    marketName: '',
    erpLocationId: undefined,
    storeNumber: '',
    storeName: '',
    emailAddress: '',
    phoneNumber: '',
    shipToLocation: '',
    shipToAddress: '',
    town: '',
    county: '',
    country: '',
    postCode: '',
    inventoryEnabled: false,
    inventoryTestEnabled: false,
    inventoryStartDate: '',
    isParEnabled: undefined,
    catalogueIds: [],
    odsConfiguration: [],
    suppliersAccountNumber: [],
    orderTypesAccountNumber: [],
  });

  const [instances, setInstances] = useState<Instance[]>([]);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [catalogues, setCatalogues] = useState<MarketCatalogue[]>([]);
  const [orderTypes, setOrderTypes] = useState<MarketOrderType[]>([]);
  const [marketSuppliers, setMarketSuppliers] = useState<MarketSupplier[]>([]);
  
  const isOfs = selectedSystem?.name === 'Orderly for Stores';

  useEffect(() => {
    loadInstances();
    if (isEditMode && id) {
      loadStore(id);
    } else {
      setIsLoading(false);
    }
  }, [isEditMode, id]);

  useEffect(() => {
    if (formData.companyId > 0) {
      loadMarkets(formData.companyId.toString());
    }
  }, [formData.companyId]);

  useEffect(() => {
    if (formData.companyId > 0 && formData.customerTypeId > 0) {
      loadCatalogues(formData.companyId.toString());
    }
  }, [formData.companyId, formData.customerTypeId]);

  useEffect(() => {
    if (formData.companyId > 0 && formData.customerTypeId > 0) {
      loadOrderTypes(formData.companyId.toString(), formData.customerTypeId.toString());
      loadMarketSuppliers(formData.companyId.toString(), formData.customerTypeId.toString());
    }
  }, [formData.companyId, formData.customerTypeId]);

  const loadInstances = async () => {
    try {
      const response = await client.getInstances({
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setInstances(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load instances');
    }
  };

  const loadMarkets = async (instanceId: string) => {
    try {
      const response = await client.getMarkets({
        companyId: instanceId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarkets(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load markets');
    }
  };

  const loadCatalogues = async (instanceId: string) => {
    try {
      const response = await client.getMarketCatalogues({
        companyId: instanceId,
        customerTypeId: formData.customerTypeId.toString(),
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setCatalogues(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load catalogues');
    }
  };

  const loadOrderTypes = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketOrderTypes({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setOrderTypes(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load order types');
    }
  };

  const loadMarketSuppliers = async (instanceId: string, marketId: string) => {
    try {
      const response = await client.getMarketSuppliers({
        companyId: instanceId,
        customerTypeId: marketId,
        pageNumber: 0,
        pageSize: 2147483647,
      });
      
      if (response.success && response.data) {
        setMarketSuppliers(response.data.items || []);
      }
    } catch (error) {
      logError('Failed to load market suppliers');
    }
  };

  const loadStore = async (storeId: string) => {
    try {
      const response = await client.getMarketStore(storeId);
      
      if (response.success && response.data) {
        setFormData({
          ...response.data,
          catalogueIds: response.data.catalogueIds || [],
          suppliersAccountNumber: response.data.suppliersAccountNumber || [],
          orderTypesAccountNumber: response.data.orderTypesAccountNumber || [],
          inventoryStartDate: response.data.inventoryStartDate,
          companyId: response.data.companyId || 0,
          customerTypeId: response.data.customerTypeId || 0,
        });
      } else {
        setErrorMessage(response.message || t('loadErrorMessage'));
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError('Failed to load store');
      setErrorMessage(t('loadErrorMessage'));
      setShowErrorMessage(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const handleNumberInputChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value === '' ? undefined : Number(event.target.value);
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSwitchChange = (field: keyof MarketStore) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.checked,
    }));
  };

  const handleInstanceChange = (event: React.SyntheticEvent, value: Instance | null) => {
    setFormData(prev => ({
      ...prev,
      companyId: value?.id || 0,
      instanceName: value?.name || '',
      customerTypeId: 0,
      marketName: '',
      catalogueIds: [],
      odsConfiguration: [],
      suppliersAccountNumber: [],
      orderTypesAccountNumber: [],
    }));
    
    setMarkets([]);
    setCatalogues([]);
    setOrderTypes([]);
    setMarketSuppliers([]);
    
    if (value?.id) {
      loadMarkets(value.id.toString());
    }
  };

  const handleMarketChange = (event: React.SyntheticEvent, value: Market | null) => {
    const marketId = value?.customerTypeId || 0;
    setFormData(prev => ({
      ...prev,
      customerTypeId: marketId,
      marketName: value?.name || '',
      catalogueIds: [],
      odsConfiguration: [],
      suppliersAccountNumber: [],
      orderTypesAccountNumber: [],
    }));
    
    setCatalogues([]);
    setOrderTypes([]);
    setMarketSuppliers([]);
  };

  const handleDeliveryScheduleChange = (orderTypeId: number, deliveryDay: number, daysToCutoff: number) => {
    setFormData(prev => {
      const newOdsConfiguration = [...(prev.odsConfiguration || [])];
      const existingIndex = newOdsConfiguration.findIndex(
        ods => ods.orderTypeId === orderTypeId && ods.deliveryDay === deliveryDay,
      );

      if (existingIndex >= 0) {
        if (daysToCutoff === 0) {
          newOdsConfiguration.splice(existingIndex, 1);
        } else {
          newOdsConfiguration[existingIndex] = {
            ...newOdsConfiguration[existingIndex],
            daysToCutoff: daysToCutoff.toString(),
          };
        }
      } else if (daysToCutoff > 0) {
        newOdsConfiguration.push({
          deliveryScheduleRuleId: undefined,
          orderTypeId: orderTypeId,
          orderType: orderTypes.find(ot => ot.orderTypeId === orderTypeId)?.name || '',
          week: 0,
          deliveryDay: deliveryDay,
          daysToCutoff: daysToCutoff.toString(),
          supplierNumber: '',
          supplierName: '',
        });
      }

      return {
        ...prev,
        odsConfiguration: newOdsConfiguration,
      };
    });
  };

  const handleSupplierDeliveryScheduleChange = (
    orderTypeId: number,
    deliveryDay: number,
    daysToCutoff: number,
    supplierName?: string | null,
    supplierNumber?: string | null,
  ) => {
    setFormData(prev => {
      const newOdsConfiguration = [...(prev.odsConfiguration || [])];
      const existingIndex = newOdsConfiguration.findIndex(
        ods => ods.orderTypeId === orderTypeId && 
               ods.deliveryDay === deliveryDay &&
               (supplierNumber
                 ? ods.supplierNumber === supplierNumber
                 : !ods.supplierNumber),
      );

      if (existingIndex >= 0) {
        if (daysToCutoff === 0) {
          newOdsConfiguration.splice(existingIndex, 1);
        } else {
          newOdsConfiguration[existingIndex] = {
            ...newOdsConfiguration[existingIndex],
            daysToCutoff: daysToCutoff.toString(),
          };
        }
      } else if (daysToCutoff > 0) {
        const existingConfig = newOdsConfiguration.find(
          config => config.orderTypeId === orderTypeId &&
                    (supplierNumber
                      ? config.supplierNumber === supplierNumber
                      : !config.supplierNumber),
        );
        
        newOdsConfiguration.push({
          deliveryScheduleRuleId: undefined,
          orderTypeId: orderTypeId,
          orderType: orderTypes.find(ot => ot.orderTypeId === orderTypeId)?.name || '',
          week: existingConfig?.week || 1,
          deliveryDay: deliveryDay,
          daysToCutoff: daysToCutoff.toString(),
          supplierNumber: supplierNumber || '',
          supplierName: supplierName || '',
        });
      }

      return {
        ...prev,
        odsConfiguration: newOdsConfiguration,
      };
    });
  };

  const handleCatalogueToggle = (catalogueId: number) => {
    setFormData(prev => ({
      ...prev,
      catalogueIds: prev.catalogueIds.includes(catalogueId)
        ? prev.catalogueIds.filter(id => id !== catalogueId)
        : [...prev.catalogueIds, catalogueId],
    }));
  };

  const getUrlParams = (): URLSearchParams => {
    const params = new URLSearchParams();
    if (companyId) params.set('companyId', companyId);
    if (customerTypeId) params.set('customerTypeId', customerTypeId);
    if (searchTerm) params.set('searchTerm', searchTerm);

    return params;
  };

  const handleCancel = () => {
    const params = getUrlParams();
    navigate(`/estate-management/sites${params.toString() ? `?${params.toString()}` : ''}`);
  };

  const handleSubmit = async () => {
    setIsSaving(true);
    setShowSuccessMessage(false);
    setShowErrorMessage(false);

    try {
      let response;
      
      if (isEditMode && id) {
        response = await client.updateMarketStore(id, formData);
      } else {
        response = await client.createMarketStore(formData);
      }

      if (response.success) {
        setShowSuccessMessage(true);
        setTimeout(() => {
          const params = getUrlParams();
          navigate(`/estate-management/sites${params.toString() ? `?${params.toString()}` : ''}`);
        }, 500);
      } else if (response.message) {
        setErrorMessage(response.message);
        setShowErrorMessage(true);
      }
    } catch (error) {
      logError(isEditMode ? 'Failed to update store' : 'Failed to create store');
      const errorMessage = extractErrorMessage(error);
      setErrorMessage(errorMessage || (isEditMode ? t('updateErrorMessage') : t('createErrorMessage')));
      setShowErrorMessage(true);
    } finally {
      setIsSaving(false);
    }
  };

  const renderDeliveryScheduleTable = () => {
    const days = [
      { key: 1, label: t('day1'), fullName: 'Monday' },
      { key: 2, label: t('day2'), fullName: 'Tuesday' },
      { key: 3, label: t('day3'), fullName: 'Wednesday' },
      { key: 4, label: t('day4'), fullName: 'Thursday' },
      { key: 5, label: t('day5'), fullName: 'Friday' },
      { key: 6, label: t('day6'), fullName: 'Saturday' },
      { key: 7, label: t('day7'), fullName: 'Sunday' },
    ];

    const getCutoffOptions = (deliveryDay: number) => {
      const options = [{ value: 0, label: 'No Delivery' }];
      for (let i = 1; i <= 6; i++) {
        let cutoffDay = deliveryDay - i;
        if (cutoffDay <= 0) {
          cutoffDay += 7;
        }     
        const dayNames = ['', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
        options.push({
          value: i,
          label: `Cut off ${dayNames[cutoffDay]}`,
        });
      }
      return options;
    };

    // Non-DSD order types: one row per order type
    const nonDsdRows = orderTypes
      .filter(ot => !isOrderTypeDSD(ot.orderTypeId))
      .map(orderType => ({
        ...orderType,
        displayName: orderType.name,
        supplierName: '',
        supplierNumber: '',
        isSupplierSpecific: false,
      }));

    // DSD order types: match each to its specific supplier, or expand with all suppliers
    const dsdTypes = orderTypes.filter(ot => isOrderTypeDSD(ot.orderTypeId));
    const dsdSupplierRows = (() => {
      if (dsdTypes.length === 0) return [];

      // Try to match each DSD order type to a specific market supplier via cutoff.supplierName or name
      const matchedSupplierNumbers = new Set<string>();
      const specificRows: typeof nonDsdRows = [];

      for (const dsdType of dsdTypes) {
        const matchName = dsdType.cutoff?.supplierName || dsdType.name;
        const matched = matchName
          ? marketSuppliers.find(s => s.supplierName === matchName)
          : undefined;

        if (matched) {
          matchedSupplierNumbers.add(matched.supplierNumber);
          specificRows.push({
            ...dsdType,
            displayName: dsdType.name,
            supplierName: matched.supplierName,
            supplierNumber: matched.supplierNumber,
            isSupplierSpecific: true,
          });
        }
      }

      // DSD types that didn't match a specific supplier get expanded with remaining suppliers
      const unmatchedDsdTypes = dsdTypes.filter(dsdType => {
        const matchName = dsdType.cutoff?.supplierName || dsdType.name;
        return !matchName || !marketSuppliers.find(s => s.supplierName === matchName);
      });

      const genericRows: typeof nonDsdRows = [];
      for (const dsdType of unmatchedDsdTypes) {
        const supplierMap = new Map<string, { supplierName: string; supplierNumber: string }>();

        // Add remaining market suppliers not already matched 1:1
        marketSuppliers
          .filter(s => !matchedSupplierNumbers.has(s.supplierNumber))
          .forEach(s => supplierMap.set(s.supplierNumber, { supplierName: s.supplierName, supplierNumber: s.supplierNumber }));

        Array.from(supplierMap.values()).forEach(supplier => {
          genericRows.push({
            ...dsdType,
            displayName: dsdType.name,
            supplierName: supplier.supplierName,
            supplierNumber: supplier.supplierNumber,
            isSupplierSpecific: true,
          });
        });
      }

      return [...specificRows, ...genericRows];
    })();

    const displayOrderTypes = [...nonDsdRows, ...dsdSupplierRows];

    const findConfiguration = (orderTypeId: number, deliveryDay: number, supplierName?: string | null, supplierNumber?: string | null) => {
      return (formData.odsConfiguration || []).find(
        ods => ods.orderTypeId === orderTypeId && 
               ods.deliveryDay === deliveryDay &&
               (supplierNumber
                 ? ods.supplierNumber === supplierNumber
                 : !ods.supplierNumber),
      );
    };

    return (
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>{t('orderType')}</TableCell>
              {days.map(day => (
                <TableCell
                  key={day.key}
                  align="center"
                  sx={{ minWidth: 150 }}
                >
                  {day.fullName} Delivery
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {displayOrderTypes.map((orderType, index) => (
              <TableRow key={`${orderType.orderTypeId}-${orderType.supplierName || 'default'}-${index}`}>
                <TableCell>
                  {orderType.displayName}
                  {orderType.isSupplierSpecific && (
                    <Typography 
                      sx={{ fontSize: '0.75rem' }}
                      display="block" 
                      color="text.secondary"
                    >
                      {orderType.supplierName}
                    </Typography>
                  )}
                </TableCell>
                {days.map(day => {
                  const config = findConfiguration(orderType.orderTypeId, day.key, orderType.supplierName, orderType.supplierNumber);
                  const currentValue = config ? Number(config.daysToCutoff) : 0;

                  return (
                    <TableCell
                      key={day.key}
                      align="center"
                    >
                      <FormControl 
                        fullWidth 
                        size="small"
                      >
                        <Select
                          value={currentValue}
                          onChange={(event: SelectChangeEvent<number>) => {
                            const newValue = Number(event.target.value);
                            if (orderType.isSupplierSpecific) {
                              handleSupplierDeliveryScheduleChange(
                                orderType.orderTypeId, 
                                day.key, 
                                newValue,
                                orderType.supplierName,
                                orderType.supplierNumber,
                              );
                            } else {
                              handleDeliveryScheduleChange(
                                orderType.orderTypeId, 
                                day.key, 
                                newValue,
                              );
                            }
                          }}
                          displayEmpty
                        >
                          {getCutoffOptions(day.key).map(option => (
                            <MenuItem 
                              key={option.value} 
                              value={option.value}
                            >
                              {option.label}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    );
  };

  const overviewContent = (
    <Stack spacing={8}>
      {/* Basic Store Information */}
      <Box>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{ mb: 4 }}
        >
          {t('management.basicInformation')}
        </Typography>
        <Box>
          <Grid
            container
            spacing={8}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('storeName')}
                value={formData.storeName}
                onChange={handleInputChange('storeName')}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
              
            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('storeNumber')}
                value={formData.storeNumber}
                onChange={handleInputChange('storeNumber')}
                required
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('emailAddress')}
                type="email"
                value={formData.emailAddress}
                onChange={handleInputChange('emailAddress')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('phoneNumber')}
                value={formData.phoneNumber}
                onChange={handleInputChange('phoneNumber')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('erpLocationId')}
                type="number"
                value={formData.erpLocationId || ''}
                onChange={handleNumberInputChange('erpLocationId')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Location Details */}
      <Box>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{ mb: 4 }}
        >
          {t('management.locationDetails')}
        </Typography>
        <Box>
          <Grid
            container
            spacing={8}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('shipToLocation')}
                value={formData.shipToLocation}
                onChange={handleInputChange('shipToLocation')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
              
            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('shipToAddress')}
                value={formData.shipToAddress}
                onChange={handleInputChange('shipToAddress')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('town')}
                value={formData.town}
                onChange={handleInputChange('town')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('county')}
                value={formData.county}
                onChange={handleInputChange('county')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('country')}
                value={formData.country}
                onChange={handleInputChange('country')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('postCode')}
                value={formData.postCode}
                onChange={handleInputChange('postCode')}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Instance and Market Configuration */}
      <Box>
        <Typography
          variant="textMD"
          fontWeight={600}
          sx={{ mb: 4 }}
        >
          {t('configuration')}
        </Typography>
        <Box>
          <Grid
            container
            spacing={8}
          >
            <Grid
              item
              xs={12}
              md={6}
            >
              <Autocomplete
                options={instances}
                getOptionLabel={(option) => option.name}
                value={instances.find(instance => instance.id === formData.companyId) || null}
                onChange={handleInstanceChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('instance')}
                    required
                  />
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <Autocomplete
                options={markets}
                getOptionLabel={(option) => option.name}
                value={markets.find(market => market.customerTypeId === formData.customerTypeId) || null}
                onChange={handleMarketChange}
                disabled={!formData.companyId}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={t('market')}
                    required
                  />
                )}
              />
            </Grid>

            <Grid
              item
              xs={12}
              md={6}
            >
              <TextField
                fullWidth
                label={t('inventoryStartDate')}
                type="date"
                value={formData.inventoryStartDate || ''}
                onChange={handleInputChange('inventoryStartDate')}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  },
                }}
              />
            </Grid>

            <Grid
              item
              xs={12}
            >
              <Grid
                container
                spacing={4}
              >
                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.inventoryEnabled || false}
                        onChange={handleSwitchChange('inventoryEnabled')}
                      />
                    }
                    label={
                      <Typography
                        variant="textMD"
                        sx={{
                          ml: 1,
                        }}
                      >
                        {t('inventoryEnabled')}
                      </Typography>
                    }
                    sx={{
                      m: 0,
                      p: 2,
                      backgroundColor: 'background.paper',
                      alignItems: 'center',
                    }}
                  />
                </Grid>

                <Grid
                  item
                  xs={12}
                  md={6}
                >
                  <FormControlLabel
                    control={
                      <Switch
                        checked={formData.inventoryTestEnabled || false}
                        onChange={handleSwitchChange('inventoryTestEnabled')}
                      />
                    }
                    label={
                      <Typography
                        variant="textMD"
                        sx={{
                          ml: 1,
                        }}
                      >
                        {t('inventoryTestEnabled')}
                      </Typography>
                    }
                    sx={{
                      m: 0,
                      p: 2,
                      backgroundColor: 'background.paper',
                      alignItems: 'center',
                    }}
                  />
                </Grid>

                {isOfs && (
                  <Grid
                    item
                    xs={12}
                    md={6}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={formData.isParEnabled ?? false}
                          onChange={handleSwitchChange('isParEnabled')}
                        />
                      }
                      label={
                        <Typography
                          variant="textMD"
                          sx={{
                            ml: 1,
                          }}
                        >
                          {t('isParEnabled')}
                        </Typography>
                      }
                      sx={{
                        m: 0,
                        p: 2,
                        backgroundColor: 'background.paper',
                        alignItems: 'center',
                      }}
                    />
                  </Grid>
                )}
                 
                {marketSuppliers?.length > 0 && <Grid item 
                  xs={12}
                  md={12}>
                  <Typography sx={{ mb: 4 }}>
                    {t('suppliersAccountNumber')}
                  </Typography>
                  <Grid container 
                    spacing={4}>
                    {marketSuppliers.map((s) => {
                      const existing = (formData.suppliersAccountNumber || []).find(acc => acc.name === s.supplierNumber);
                      return (
                        <Grid item 
                          xs={12} 
                          md={6} 
                          key={s.supplierNumber}>
                          <TextField
                            label={`${s.supplierName} - ${s.supplierNumber}`}
                            value={existing?.value ?? ''}
                            onChange={e => {
                              const value = e.target.value;
                              setFormData(prev => {
                                const current = prev.suppliersAccountNumber || [];
                                const i = current.findIndex(a => a.name === s.supplierNumber);
                                if (i >= 0) {
                                  const updated = [...current];
                                  updated[i] = { ...updated[i], value };
                                  return { ...prev, suppliersAccountNumber: updated };
                                }
                                return { ...prev, suppliersAccountNumber: [...current, { id: 0, name: s.supplierNumber, value }] };
                              });
                            }}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
                }

                {orderTypes?.length > 0 && <Grid item 
                  xs={12} 
                  md={12}>
                  <Typography sx={{ mb: 4 }}>
                    {t('ordertypesAccountNumber')}
                  </Typography>
                  <Grid container 
                    spacing={4}>
                    {orderTypes.map(ot => {
                      const existing = (formData.orderTypesAccountNumber || []).find(acc => acc.name === ot.orderTypeId.toString());
                      return (
                        <Grid item 
                          xs={12} 
                          md={6} 
                          key={ot.orderTypeId}>
                          <TextField
                            label={ot.name}
                            value={existing?.value ?? ''}
                            onChange={e => {
                              const value = e.target.value;
                              setFormData(prev => {
                                const current = prev.orderTypesAccountNumber || [];
                                const i = current.findIndex(a => a.name === ot.orderTypeId.toString());
                                if (i >= 0) {
                                  const updated = [...current];
                                  updated[i] = { ...updated[i], value };
                                  return { ...prev, orderTypesAccountNumber: updated };
                                }
                                return { ...prev, orderTypesAccountNumber: [...current, { id: 0, name: ot.orderTypeId.toString(), value }] };
                              });
                            }}
                            fullWidth
                            size="small"
                          />
                        </Grid>
                      );
                    })}
                  </Grid>
                </Grid>
                }
              </Grid>
            </Grid>
          </Grid>
        </Box>
      </Box>

      {/* Catalogue Configuration */}
      {formData.companyId > 0 && formData.customerTypeId > 0 && (
        <Box>
          <Typography
            variant="textMD"
            fontWeight={600}
            sx={{ mb: 4 }}
          >
            {t('management.catalogueConfiguration')}
          </Typography>
          <Typography
            sx={{
              mb: 4,
            }}
          >
            {t('selectCatalogues')}
          </Typography>
          <Grid 
            container 
            spacing={2}
          >
            {catalogues.map((catalogue) => (
              <Grid 
                item 
                xs={12} 
                sm={6} 
                md={4} 
                key={catalogue.customerGroupId}
              >
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData.catalogueIds.includes(catalogue.customerGroupId)}
                      onChange={() => handleCatalogueToggle(catalogue.customerGroupId)}
                    />
                  }
                  label={catalogue.name}
                />
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Delivery Schedule Configuration */}
      {formData.companyId > 0 && formData.customerTypeId > 0 && orderTypes.length > 0 && (
        <Box>
          <Typography
            variant="textMD"
            fontWeight={600}
            sx={{ mb: 4 }}
          >
            {t('management.deliveryScheduleConfiguration')}
          </Typography>
          <Typography
            sx={{
              mb: 4,
            }}
          >
            {t('orderDeliverySchedule')}
          </Typography>
          {renderDeliveryScheduleTable()}
        </Box>
      )}
    </Stack>
  );

  const tabPanels = useMemo(() => [
    {
      key: 'overview',
      label: tc('siteManagementTabOverview', 'Overview'),
      content: overviewContent,
    },
    {
      key: 'customerContract',
      label: tc('siteManagementTabCustomerContract', 'Customer & Contract'),
      content: (
        <Grid container
          spacing={8}>
          <Grid item
            xs={12}
            md={6}>
            <TextField
              fullWidth
              disabled
              label={tc('siteManagementCustomer', 'Customer')}
              value={EXAMPLE.customer}
            />
          </Grid>
          <Grid item
            xs={12}
            md={6}>
            <TextField
              fullWidth
              disabled
              label={tc('siteManagementContract', 'Contract')}
              value={EXAMPLE.contract}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'operations',
      label: tc('siteManagementTabOperations', 'Operations'),
      content: (
        <Grid container
          spacing={8}>
          <Grid item
            xs={12}
            md={4}>
            <TextField
              fullWidth
              disabled
              label={t('instance')}
              value={formData.instanceName || '-'}
            />
          </Grid>
          <Grid item
            xs={12}
            md={4}>
            <TextField
              fullWidth
              disabled
              label={t('market')}
              value={formData.marketName || '-'}
            />
          </Grid>
          <Grid item
            xs={12}
            md={4}>
            <TextField
              fullWidth
              disabled
              label={t('inventoryEnabled')}
              value={formData.inventoryEnabled ? t('yes') : t('no')}
            />
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'zones',
      label: tc('siteManagementTabZones', 'Zones'),
      content: (
        <TableContainer component={Paper}
          elevation={0}
          variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{tc('siteManagementZoneName', 'Zone Name')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {EXAMPLE.zones.map((z) => (
                <TableRow key={z}>
                  <TableCell>{z}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
    },
    {
      key: 'equipment',
      label: tc('siteManagementTabEquipment', 'Equipment'),
      content: (
        <Grid container
          spacing={8}>
          <Grid item
            xs={12}
            md={4}>
            <TextField
              fullWidth
              disabled
              label={tc('siteManagementEquipmentAssigned', 'Equipment Assigned')}
              value={EXAMPLE.equipmentCount}
            />
          </Grid>
          <Grid item
            xs={12}>
            <Typography variant="textSM"
              color="text.secondary">
              {tc('siteManagementEquipmentHint', 'See Site Equipment for the full asset register.')}
            </Typography>
          </Grid>
        </Grid>
      ),
    },
    {
      key: 'users',
      label: tc('siteManagementTabUsers', 'Users'),
      content: (
        <TableContainer component={Paper}
          elevation={0}
          variant="outlined">
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{tc('siteManagementUserName', 'Name')}</TableCell>
                <TableCell>{tc('siteManagementUserRole', 'Role')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {EXAMPLE.users.map((u) => (
                <TableRow key={u.name}>
                  <TableCell>{u.name}</TableCell>
                  <TableCell>{u.role}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      ),
    },
    {
      key: 'procurementSetup',
      label: tc('siteManagementTabProcurement', 'Procurement Setup'),
      content: (
        <TextField
          fullWidth
          disabled
          multiline
          minRows={2}
          label={tc('siteManagementTabProcurement', 'Procurement Setup')}
          value={EXAMPLE.procurement}
        />
      ),
    },
    {
      key: 'compliance',
      label: tc('siteManagementTabCompliance', 'Compliance'),
      content: (
        <TextField
          fullWidth
          disabled
          multiline
          minRows={2}
          label={tc('siteManagementTabCompliance', 'Compliance')}
          value={EXAMPLE.compliance}
        />
      ),
    },
    {
      key: 'reporting',
      label: tc('siteManagementTabReporting', 'Reporting'),
      content: (
        <TextField
          fullWidth
          disabled
          multiline
          minRows={2}
          label={tc('siteManagementTabReporting', 'Reporting')}
          value={EXAMPLE.reporting}
        />
      ),
    },
    {
      key: 'documents',
      label: tc('siteManagementTabDocuments', 'Documents'),
      content: (
        <Typography variant="textMD"
          color="text.secondary">
          {tc('siteManagementDocumentsHint', 'Site lease, compliance certificates and customer agreements are stored here.')}
        </Typography>
      ),
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, tc, overviewContent, formData.instanceName, formData.marketName, formData.inventoryEnabled]);

  if (isLoading) {
    return (
      <PageArea>
        <PageTitle>{t(isEditMode ? 'management.editStore' : 'management.createStore')}</PageTitle>
        <Typography>{t('management.loading')}</Typography>
      </PageArea>
    );
  }

  return (
    <PageArea>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 4,
          flexWrap: 'wrap',
          mb: 6,
        }}
      >
        <Box>
          <PageTitle>{t(isEditMode ? 'management.editStore' : 'management.createStore')}</PageTitle>
          <Typography
            variant="textSM"
            color="text.secondary"
            sx={{ mt: 1 }}
          >
            {t('subtitle')}
          </Typography>
        </Box>

        <Stack
          direction="row"
          spacing={3}
          justifyContent="flex-end"
        >
          <Button
            variant="secondary"
            size="lg"
            onClick={handleCancel}
            disabled={isSaving}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="primary"
            size="lg"
            onClick={handleSubmit}
            disabled={isSaving || !formData.storeName || !formData.storeNumber || !formData.companyId || !formData.customerTypeId}
          >
            {isSaving ? t('saving') : (isEditMode ? t('update') : t('create'))}
          </Button>
        </Stack>
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_event, newValue) => setActiveTab(newValue)}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          mb: '-1px',
        }}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
      >
        {tabPanels.map((panel) => (
          <Tab key={panel.key}
            label={panel.label} />
        ))}
      </Tabs>

      <Section style={{ mt: 0, p: 5, pb: 0 }}>
        <Grid container
          spacing={8}>
          {tabPanels.map((panel, index) => (
            <Grid item
              xs={12}
              key={panel.key}
              sx={{ display: activeTab === index ? 'block' : 'none' }}>
              <Box sx={{ p: 4 }}>
                {panel.content}
              </Box>
            </Grid>
          ))}
        </Grid>
      </Section>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={showSuccessMessage}
        autoHideDuration={5000}
        onClose={() => setShowSuccessMessage(false)}
      >
        <Alert
          onClose={() => setShowSuccessMessage(false)}
          severity="success"
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {isEditMode ? t('updateSuccessMessage') : t('createSuccessMessage')}
        </Alert>
      </Snackbar>

      <Snackbar
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        open={showErrorMessage}
        autoHideDuration={5000}
        onClose={() => setShowErrorMessage(false)}
      >
        <Alert
          onClose={() => setShowErrorMessage(false)}
          severity="error"
          variant="filled"
          sx={{
            width: '100%',
          }}
        >
          {errorMessage}
        </Alert>
      </Snackbar>
    </PageArea>
  );
};

export default SiteDetail;
